import React from "react";
import { ChevronLeft, ChevronRight, CheckCircle2, AlertTriangle, Pencil } from "lucide-react";
import { gbp } from "../../lib/format";
import { QuietButton } from "../UI";
import { ACTION_TONE } from "./importShared";
import { ImportRowFix } from "./ImportRowFix";

export function ActionChip({ action }) {
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded border font-mono uppercase ${ACTION_TONE[action] || ACTION_TONE.unchanged}`}
          style={{ fontSize: 10, letterSpacing: 0.4 }}>{action}</span>
  );
}

const FILTERS = [["", "All"], ["create", "Create"], ["update", "Update"], ["unchanged", "Unchanged"], ["reject", "Rejected"]];

/* LT-19b §4 / F5 — paged, never renders the whole file at once.
 * LT-19d — on a draft, every row can be edited before Apply (`editable`,
 * `onEdit`); a structured server `fix` gets a one-click button (`onFix`). */
export function ImportRowsTable({ rows, total, page, pageSize, onPage, filter, onFilter, showWritten, writtenFilter, onWrittenFilter, showUndo,
                                  editable = false, onEdit, onFix, fixing, lookups }) {
  const pages = Math.max(1, Math.ceil((total || 0) / pageSize));
  const cols = 5 + (showWritten ? 1 : 0) + (showUndo ? 1 : 0) + (editable ? 1 : 0);
  return (
    <div className="space-y-2" data-testid="import-rows-table">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-1" data-testid="import-rows-filter">
          {FILTERS.map(([v, l]) => (
            <button key={v} type="button" onClick={() => onFilter(v)}
              className={"px-2 py-1 rounded border " + (filter === v ? "border-accent text-ink bg-accent/5" : "border-line text-ink-3 hover:text-ink")}
              style={{ fontSize: 11.5 }} data-testid={`import-rows-filter-${v || "all"}`}>{l}</button>
          ))}
          {showWritten && (
            <select value={writtenFilter} onChange={(e) => onWrittenFilter(e.target.value)} className="ml-2 h-7 rounded border border-line bg-surface text-ink-2 px-1"
                    style={{ fontSize: 11.5 }} data-testid="import-rows-written-filter">
              <option value="">written + unwritten</option>
              <option value="true">written rows</option>
              <option value="false">unwritten rows</option>
            </select>
          )}
        </div>
        <div className="flex items-center gap-1 text-ink-3" style={{ fontSize: 11.5 }} data-testid="import-rows-pager">
          <span>{total} row{total === 1 ? "" : "s"} · page {page} of {pages}</span>
          <QuietButton icon={ChevronLeft} onClick={() => onPage(page - 1)} disabled={page <= 1} data-testid="import-rows-prev" />
          <QuietButton icon={ChevronRight} onClick={() => onPage(page + 1)} disabled={page >= pages} data-testid="import-rows-next" />
        </div>
      </div>
      <div className="rounded border border-line overflow-hidden">
        <table className="w-full" style={{ fontSize: 12.5 }}>
          <thead className="bg-surface-2/60 text-ink-3 uppercase" style={{ fontSize: 10.5, letterSpacing: 0.4 }}>
            <tr>
              <th className="text-left px-3 py-2 w-12">Row</th>
              <th className="text-left px-2 py-2">Material</th>
              <th className="text-left px-2 py-2 w-24">Action</th>
              <th className="text-left px-2 py-2 w-40">Price</th>
              <th className="text-left px-2 py-2">Reason / warnings</th>
              {showWritten && <th className="text-left px-2 py-2 w-16">Wrote</th>}
              {showUndo && <th className="text-left px-2 py-2 w-56">Undo</th>}
              {editable && <th className="px-2 py-2 w-16" />}
            </tr>
          </thead>
          <tbody>
            {(rows || []).map((r) => (
              <tr key={r.row_number} className="border-t border-line-soft align-top" data-testid={`import-row-${r.row_number}`}>
                <td className="px-3 py-1.5 font-mono tnum text-ink-3">{r.row_number}</td>
                <td className="px-2 py-1.5">
                  <div className="text-ink flex items-center gap-2">
                    <span>{r.material_name || r.raw?.name || "—"}</span>
                    {r.edited && (
                      <span className="inline-flex items-center px-1 py-px rounded border border-amber-200 bg-amber-50 text-amber-800 font-mono uppercase"
                        style={{ fontSize: 9, letterSpacing: 0.4 }} title={`Edited in the preview: ${Object.keys(r.edited_cells || {}).join(", ")}`}
                        data-testid={`import-row-${r.row_number}-edited`}>edited</span>
                    )}
                  </div>
                  {r.material_code && <div className="font-mono text-ink-4" style={{ fontSize: 10.5 }}>{r.material_code}</div>}
                </td>
                <td className="px-2 py-1.5"><ActionChip action={r.action} /></td>
                <td className="px-2 py-1.5 font-mono tnum text-ink-2">
                  {r.action === "update" && r.new_price != null ? `${gbp(r.old_price)} → ${gbp(r.new_price)}`
                    : r.action === "create" && r.new_price != null ? gbp(r.new_price)
                    : r.action === "update" ? "unchanged price" : "—"}
                </td>
                <td className="px-2 py-1.5">
                  {r.reject_reason && <div className="text-red-800" data-testid={`import-row-${r.row_number}-reason`}>{r.reject_reason}</div>}
                  {editable && !r.applied_at && <ImportRowFix row={r} lookups={lookups} fixing={fixing} onFix={onFix} />}
                  {(r.warnings || []).map((w, i) => (
                    <div key={i} className="text-amber-800 inline-flex items-start gap-1" data-testid={`import-row-${r.row_number}-warning`}>
                      <AlertTriangle size={12} className="mt-0.5 shrink-0" />{w}
                    </div>
                  ))}
                  {r.action === "update" && !r.reject_reason && (
                    <div className="text-ink-4 font-mono" style={{ fontSize: 10.5 }}>
                      {Object.keys(r.changes || {}).filter((k) => k !== "current_price").join(", ")}
                    </div>
                  )}
                </td>
                {showWritten && (
                  <td className="px-2 py-1.5 text-emerald-700">{r.applied_at ? <CheckCircle2 size={14} /> : <span className="text-ink-4">—</span>}</td>
                )}
                {showUndo && (
                  <td className="px-2 py-1.5" style={{ fontSize: 11 }} data-testid={`import-row-${r.row_number}-undo`}>
                    {r.rollback ? (
                      <>
                        <span className="font-mono uppercase text-ink-2" style={{ fontSize: 10 }}>{String(r.rollback.op).replace("_", " ")}</span>
                        {r.rollback.fields_reverted?.length > 0 && <span className="ml-1 text-ink-4 font-mono" style={{ fontSize: 10 }}>{r.rollback.fields_reverted.join(", ")}</span>}
                        {r.rollback.note && <div className="text-amber-800">{r.rollback.note}</div>}
                      </>
                    ) : (r.action === "create" || r.action === "update") && r.applied_at ? <span className="text-ink-4">pending</span> : <span className="text-ink-4">—</span>}
                  </td>
                )}
                {editable && (
                  <td className="px-2 py-1.5 text-right">
                    {!r.applied_at && (
                      <button type="button" onClick={() => onEdit?.(r)} title="Edit this row before applying"
                        className="inline-flex items-center gap-1 text-ink-3 hover:text-accent" style={{ fontSize: 11.5 }}
                        data-testid={`import-row-${r.row_number}-edit`}>
                        <Pencil size={12} /> Edit
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
            {(rows || []).length === 0 && (
              <tr><td colSpan={cols} className="px-3 py-6 text-center text-ink-4" style={{ fontSize: 12 }}>No rows match this filter.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
